import type { BuildingTypeId } from '../buildings/definitions';
import type { UnitTypeId } from '../units/unitDefs';

/**
 * Season pass (D112): 30 tiers earned with XP. Two tracks — FREE for everyone,
 * PREMIUM only when the server has granted it (a purchase verified server-side;
 * the payment provider is NOT connected yet). Gameplay content on the premium
 * track also arrives on the free track a few tiers later, so paying gives early
 * access, not exclusive power.
 *
 * Shared by the client and the room server: the server decides each player's
 * unlocks for an online match, never the client.
 */
export type GatedContent = Extract<UnitTypeId, 'sniper' | 'flamer'> | Extract<BuildingTypeId, 'flame_tower'>;
export const GATED: readonly GatedContent[] = ['sniper', 'flamer', 'flame_tower'];

export type Reward =
  | { readonly kind: 'unlock'; readonly content: GatedContent }
  | { readonly kind: 'skin'; readonly id: string; readonly name: string }
  | { readonly kind: 'badge'; readonly name: string };

export interface PassTier {
  readonly tier: number;
  readonly free?: Reward;
  readonly premium?: Reward;
}

export const SEASON = { id: 1, name: 'Season 1 — Ashfall', tiers: 30, xpPerTier: 150 } as const;

const R = {
  unlock: (content: GatedContent): Reward => ({ kind: 'unlock', content }),
  skin: (id: string, name: string): Reward => ({ kind: 'skin', id, name }),
  badge: (name: string): Reward => ({ kind: 'badge', name }),
};

export const PASS_TIERS: readonly PassTier[] = Array.from({ length: SEASON.tiers }, (_, i): PassTier => {
  const tier = i + 1;
  const free: Record<number, Reward> = {
    2: R.badge('Survivor'),
    5: R.unlock('sniper'),
    10: R.skin('rust', 'Rust colony colours'),
    14: R.unlock('flame_tower'),
    18: R.badge('Veteran'),
    22: R.unlock('flamer'),
    30: R.badge('Ashfall Legend'),
  };
  const premium: Record<number, Reward> = {
    1: R.unlock('sniper'),
    3: R.skin('night', 'Night-ops colours'),
    6: R.unlock('flame_tower'),
    9: R.unlock('flamer'),
    12: R.skin('gold', 'Gilded Command Center'),
    20: R.skin('ember', 'Ember Flamers'),
    30: R.skin('ashfall', 'Ashfall colony set'),
  };
  return { tier, ...(free[tier] ? { free: free[tier] } : {}), ...(premium[tier] ? { premium: premium[tier] } : {}) };
});

export function passTier(xp: number): { tier: number; into: number; next: number } {
  const t = Math.max(0, Math.floor(xp / SEASON.xpPerTier));
  return t >= SEASON.tiers ? { tier: SEASON.tiers, into: 0, next: 0 } : { tier: t, into: Math.floor(xp) - t * SEASON.xpPerTier, next: SEASON.xpPerTier };
}

/** Gameplay content unlocked at this XP (and premium entitlement). */
export function unlocksFor(xp: number, premium: boolean): GatedContent[] {
  const { tier } = passTier(xp);
  const out = new Set<GatedContent>();
  for (const t of PASS_TIERS) {
    if (t.tier > tier) break;
    if (t.free?.kind === 'unlock') out.add(t.free.content);
    if (premium && t.premium?.kind === 'unlock') out.add(t.premium.content);
  }
  return GATED.filter((g) => out.has(g));
}

export function isGated(id: string): id is GatedContent {
  return (GATED as readonly string[]).includes(id);
}

/** Skins earned at this XP (and premium entitlement), in tier order. */
export function skinsFor(xp: number, premium: boolean): { id: string; name: string }[] {
  const { tier } = passTier(xp);
  const out: { id: string; name: string }[] = [];
  for (const t of PASS_TIERS) {
    if (t.tier > tier) break;
    if (t.free?.kind === 'skin') out.push({ id: t.free.id, name: t.free.name });
    if (premium && t.premium?.kind === 'skin') out.push({ id: t.premium.id, name: t.premium.name });
  }
  return out;
}

